import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Play, Pause, Check, Gift, User, Wallet as WalletIcon, Camera, FileText, QrCode as QrCodeIcon, Share2, Award, GraduationCap } from 'lucide-react';

const OnboardingWizard = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioPlaying, setAudioPlaying] = useState(null);
  
  // Form data for each step
  const [formData, setFormData] = useState({
    displayName: '',
    timezone: '',
    preferredContact: '',
    walletAddress: '',
    avatar: null,
    selectedTemplate: null,
    qrCampaignName: '',
    messageTemplateId: null,
    sharedConfirmed: false
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const steps = [
    {
      number: 0,
      name: 'welcome',
      title: 'Welcome to Rookie Boot Camp',
      icon: Gift,
      content: 'You\'re in. 🎉 This quick setup gives you everything you need to start fast—tools, a page that converts, and a simple plan. It takes about 10 minutes.',
      audio: '/audio/onboarding/01_welcome.mp3'
    },
    {
      number: 1,
      name: 'profile',
      title: 'Set Your Basics',
      icon: User,
      content: 'Your name and time zone personalize your pages and emails. Add a contact if you\'d like.',
      audio: '/audio/onboarding/02_profile.mp3'
    },
    {
      number: 2,
      name: 'wallet',
      title: 'Add Your Payout Wallet',
      icon: WalletIcon,
      content: 'BBC uses instant, non-custodial payouts on Tron (USDT-TRC20). Paste your receiving address below.',
      audio: '/audio/onboarding/03_wallet.mp3'
    },
    {
      number: 3,
      name: 'avatar',
      title: 'Add Your Avatar',
      icon: Camera,
      content: 'A clean, friendly photo builds trust. Use a free AI headshot tool if needed (link in Cheat Sheet).',
      audio: '/audio/onboarding/04_avatar.mp3'
    },
    {
      number: 4,
      name: 'cheatsheet',
      title: 'Your Rookie Cheat Sheet',
      icon: FileText,
      content: 'This resource lists all the free tools, wallets, AI headshot creators, and autoresponders we recommend.',
      audio: '/audio/onboarding/05_cheatsheet.mp3'
    },
    {
      number: 5,
      name: 'page',
      title: 'Publish Your First Page',
      icon: FileText,
      content: 'Choose a template and we\'ll fill it with your name and avatar. Your page collects leads automatically.',
      audio: '/audio/onboarding/06_page.mp3'
    },
    {
      number: 6,
      name: 'qr',
      title: 'Turn Real World into Traffic',
      icon: QrCodeIcon,
      content: 'Generate a trackable QR code for your capture page. Print it or add to flyers and cards.',
      audio: '/audio/onboarding/07_qr.mp3'
    },
    {
      number: 7,
      name: 'share',
      title: 'Share with One Person Today',
      icon: Share2,
      content: 'Copy one of our proven messages and send it via DM or text. Mark it shared when done.',
      audio: '/audio/onboarding/08_share.mp3'
    },
    {
      number: 8,
      name: 'badge',
      title: 'Claim Your Rookie Badge',
      icon: Award,
      content: 'Complete all setup steps to earn XP and unlock the next level.',
      audio: '/audio/onboarding/09_badge.mp3'
    },
    {
      number: 9,
      name: 'bootcamp',
      title: 'Enter Boot Camp',
      icon: GraduationCap,
      content: 'You\'re ready to begin your first course: List Building 101.',
      audio: '/audio/onboarding/10_bootcamp.mp3'
    }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchProgress();
    }
  }, [isOpen]);

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/member/onboarding/progress`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.ok) {
        setProgress(data.progress);
        setCurrentStep(data.progress.current_step || 0);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStep = async (stepNumber, completed = true, stepData = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/member/onboarding/update-step`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          step_number: stepNumber,
          completed,
          data: stepData
        })
      });
      const data = await response.json();
      if (data.ok) {
        setProgress(data.progress);
        return true;
      }
    } catch (error) {
      console.error('Error updating step:', error);
    }
    return false;
  };

  const handleNext = async () => {
    // Update current step as completed
    const success = await updateStep(currentStep, true, getStepData());
    if (success) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // Completed all steps
        if (onComplete) onComplete();
        onClose();
      }
    }
  };

  const handleSkip = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${BACKEND_URL}/api/member/onboarding/skip`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (response.ok) {
      onClose();
    }
  };

  const getStepData = () => {
    const step = steps[currentStep];
    const data = {};
    
    if (step.name === 'page' && formData.selectedTemplate) {
      data.page_id = formData.selectedTemplate;
    } else if (step.name === 'qr' && formData.qrCampaignName) {
      data.qr_code_id = formData.qrCampaignName;
    } else if (step.name === 'share' && formData.messageTemplateId) {
      data.message_template_id = formData.messageTemplateId;
    }
    
    return data;
  };

  const playAudio = (stepIndex) => {
    // Placeholder for audio playback
    // Will be implemented when MP3 files are uploaded
    setAudioPlaying(stepIndex);
    setTimeout(() => setAudioPlaying(null), 3000);
  };

  const renderStepContent = () => {
    const step = steps[currentStep];

    switch (step.name) {
      case 'welcome':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🎉</div>
              <p className="text-lg text-gray-700">{step.content}</p>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <p className="text-sm text-indigo-800">
                <strong>What you'll set up:</strong> Profile, Wallet, Avatar, First Page, QR Code, and Share
              </p>
            </div>
          </div>
        );

      case 'profile':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Display Name *</label>
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                placeholder="Your name as it appears on pages"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Time Zone</label>
              <select
                value={formData.timezone}
                onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Select timezone...</option>
                <option value="America/New_York">Eastern (ET)</option>
                <option value="America/Chicago">Central (CT)</option>
                <option value="America/Denver">Mountain (MT)</option>
                <option value="America/Los_Angeles">Pacific (PT)</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Preferred Contact</label>
              <input
                type="text"
                value={formData.preferredContact}
                onChange={(e) => setFormData({...formData, preferredContact: e.target.value})}
                placeholder="Email or phone (optional)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>
        );

      case 'wallet':
        return (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> Use a personal wallet (Trust Wallet, TronLink). Never use exchange wallets.
              </p>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Tron Wallet Address (TRC-20) *</label>
              <input
                type="text"
                value={formData.walletAddress}
                onChange={(e) => setFormData({...formData, walletAddress: e.target.value})}
                placeholder="T..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm"
              />
              <p className="text-xs text-gray-500">Must start with T, 34 characters</p>
            </div>
          </div>
        );

      case 'avatar':
        return (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                {formData.avatar ? (
                  <img src={URL.createObjectURL(formData.avatar)} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <Camera className="h-12 w-12 text-gray-400" />
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFormData({...formData, avatar: e.target.files[0]})}
                className="hidden"
                id="avatar-upload"
              />
              <label
                htmlFor="avatar-upload"
                className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 cursor-pointer"
              >
                Upload Photo
              </label>
              <p className="text-sm text-gray-500 mt-2">A clean, friendly headshot works best</p>
            </div>
          </div>
        );

      case 'cheatsheet':
        return (
          <div className="space-y-4">
            <p className="text-gray-700">{step.content}</p>
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-indigo-200 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">What's Inside:</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                  Free communication tools (voicemail, conference call)
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                  Wallet setup guides (NOW Wallet, TronLink)
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                  AI tools (headshots, design, backgrounds)
                </li>
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                  Direct links to your BBC Quick List tools
                </li>
              </ul>
              <a
                href="/member/rookie-cheat-sheet"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Open Cheat Sheet
              </a>
            </div>
          </div>
        );

      case 'page':
        return (
          <div className="space-y-4">
            <p className="text-gray-700">{step.content}</p>
            <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-4">Choose a template to publish your first lead capture page:</p>
              <a
                href="/member/lead-capture/marketplace"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Browse Templates
              </a>
            </div>
          </div>
        );

      case 'qr':
        return (
          <div className="space-y-4">
            <p className="text-gray-700">{step.content}</p>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Campaign Name</label>
              <input
                type="text"
                value={formData.qrCampaignName}
                onChange={(e) => setFormData({...formData, qrCampaignName: e.target.value})}
                placeholder="e.g., 'Flyer Campaign 1'"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <a
              href="/member/qr-generator"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Create QR Code
            </a>
          </div>
        );

      case 'share':
        return (
          <div className="space-y-4">
            <p className="text-gray-700">{step.content}</p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Sample Message:</h4>
              <p className="text-sm text-gray-700 italic mb-4">
                "Hey! I'm gifting a quick-access pass to my Rookie toolbox—it shows how to start a list fast. Want the link?"
              </p>
              <button
                onClick={() => {
                  navigator.clipboard.writeText("Hey! I'm gifting a quick-access pass to my Rookie toolbox—it shows how to start a list fast. Want the link?");
                  alert('Message copied to clipboard!');
                }}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 text-sm"
              >
                Copy Message
              </button>
            </div>
            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                id="shared-confirm"
                checked={formData.sharedConfirmed}
                onChange={(e) => setFormData({...formData, sharedConfirmed: e.target.checked})}
                className="mt-1"
              />
              <label htmlFor="shared-confirm" className="text-sm text-gray-700">
                I've shared this message with at least one person
              </label>
            </div>
          </div>
        );

      case 'badge':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🏆</div>
              <p className="text-lg text-gray-700">{step.content}</p>
            </div>
            <div className="bg-white border-2 border-green-200 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3 text-center">Completion Checklist:</h3>
              <div className="space-y-2">
                {[
                  { label: 'Profile Setup', completed: progress?.step_1_profile },
                  { label: 'Wallet Added', completed: progress?.step_2_wallet },
                  { label: 'Avatar Uploaded', completed: progress?.step_3_avatar },
                  { label: 'Cheat Sheet Viewed', completed: progress?.step_4_cheatsheet },
                  { label: 'Page Published', completed: progress?.step_5_page },
                  { label: 'QR Code Created', completed: progress?.step_6_qr },
                  { label: 'Shared Once', completed: progress?.step_7_share }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center space-x-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${item.completed ? 'bg-green-500' : 'bg-gray-200'}`}>
                      {item.completed && <Check className="h-4 w-4 text-white" />}
                    </div>
                    <span className={item.completed ? 'text-gray-900' : 'text-gray-400'}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'bootcamp':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-6xl mb-4">🎓</div>
              <p className="text-lg text-gray-700">{step.content}</p>
            </div>
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6 text-center">
              <h3 className="font-semibold text-gray-900 mb-2">Ready to Level Up?</h3>
              <p className="text-gray-700 mb-4">Start with List Building 101—short, practical, and gets you moving fast.</p>
              <a
                href="/member/boot-camp-vault"
                className="inline-block px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                Enter Boot Camp
              </a>
            </div>
          </div>
        );

      default:
        return <p className="text-gray-700">{step.content}</p>;
    }
  };

  if (!isOpen || loading) return null;

  const step = steps[currentStep];
  const Icon = step.icon;
  const progressPercentage = ((currentStep + 1) / steps.length) * 100;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-white bg-opacity-20 rounded-lg p-2">
                <Icon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{step.title}</h2>
                <p className="text-indigo-100 text-sm">Step {currentStep + 1} of {steps.length}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          {/* Progress Bar */}
          <div className="bg-white bg-opacity-20 rounded-full h-2">
            <div
              className="bg-white rounded-full h-2 transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {/* Audio Player Placeholder */}
          <div className="mb-6 bg-gray-50 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              {audioPlaying === currentStep ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              <span>Audio Guide Available</span>
            </div>
            <button
              onClick={() => playAudio(currentStep)}
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              {audioPlaying === currentStep ? 'Pause' : 'Play'}
            </button>
          </div>

          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="text-gray-600 hover:text-gray-800 text-sm font-medium"
            >
              Skip for Now
            </button>
            <div className="flex items-center space-x-3">
              {currentStep > 0 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="flex items-center px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                {currentStep === steps.length - 1 ? 'Finish' : 'Continue'}
                {currentStep < steps.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
